import { LangE } from "../../models/enums";

/**
 * Chat Widget Management
 *
 * NOTE: This widget is managed directly in React code, NOT via Google Tag Manager.
 * This allows us to dynamically reload the widget when language changes without
 * page refresh. Tag Manager scripts load once and don't react to React state changes.
 *
 * The widget is initialized in App.tsx after language is determined,
 * and reloaded in LangSelect.tsx when users change language.
 */

/**
 * Updates the HTML lang attribute based on the language
 */
export const updateHtmlLangAttribute = (lang: LangE): void => {
  const langValue = lang === LangE.HE ? "he" : "en";
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("lang", langValue);
  }
};

const WIDGET_BASE_URL = "https://via.jumbomail.me/widgets/min/via.js";
const AGENT_ID = "Y3ozbVdIVytzYWg5ZTltaHRqSFJwUT09";
const API_KEY = "via_d077dcb7d9b85227522f85c145c80975";

// The widget's own DOM, addressed by exact identity (see via.js: the loader creates
// #via-widget-container > .via-iframe-container > iframe.via-chat-iframe, plus the tickets panel
// and its re-open button).
//
// ⚠️ NEVER widen these into substring selectors such as div[id*="via"] / div[class*="via"].
// Those match React-rendered nodes too: <Icon title="File sent via FTP"> on the delivery page
// renders a <Tooltip> whose DOM id is the label with non-alphanumerics stripped —
// "FilesentviaFTP" — inside a portal. Deleting a node React owns from the outside makes React's
// next unmount of that subtree throw "Failed to execute 'removeChild' on 'Node': The node to be
// removed is not a child of this node", which (there is no error boundary) takes the page down.
const WIDGET_SELECTORS = [
  "#via-widget-container",
  "#via-tickets-container",
  "#via-tickets-reopen-btn",
  ".via-tickets-reopen-btn",
  ".via-iframe-container",
  'iframe[src*="via.jumbomail.me"]',
  'iframe[src*="widget-iframe.php"]',
];

// <style> tags the widget injects into <head>. Its own destroy() clears these, but that path only
// runs when we still hold the instance, so clean them up here too.
const WIDGET_STYLE_SELECTORS = [
  "#via-widget-styles",
  "#via-widget-theme-vars",
  "#via-tickets-panel-styles",
];

/**
 * Removes the existing chat widget script and any related DOM elements
 * This works whether the widget was loaded by Tag Manager or directly
 */
export const removeChatWidget = (): void => {
  // Try to destroy stored widget instance if it exists
  if (typeof window !== "undefined" && (window as any).__viaWidgetInstance) {
    try {
      const instance = (window as any).__viaWidgetInstance;
      if (typeof instance.destroy === "function") {
        instance.destroy();
      } else if (typeof instance.remove === "function") {
        instance.remove();
      }
    } catch (e) {
      console.warn("[ChatWidget] Error destroying widget instance:", e);
    }
    delete (window as any).__viaWidgetInstance;
  }
  
  // Remove script tags from head and body
  const allScripts = [
    ...document.head.querySelectorAll(`script[src*="via.jumbomail.me/widgets/min/via.js"]`),
    ...document.body.querySelectorAll(`script[src*="via.jumbomail.me/widgets/min/via.js"]`)
  ];
  allScripts.forEach((script) => script.remove());

  // Remove widget-related DOM elements (iframes and containers) and its injected styles
  const reactRoot = document.getElementById("root");

  [...WIDGET_SELECTORS, ...WIDGET_STYLE_SELECTORS].forEach((selector) => {
    document.querySelectorAll(selector).forEach((element) => {
      // Safety net for the crash described above: React reconciles everything under #root against
      // its own copy of the tree, so we must never remove a node from in there — not even one that
      // looks like the widget's.
      if (reactRoot?.contains(element)) return;
      element.remove();
    });
  });
};

/**
 * Builds the widget configuration object
 * Note: Language is set via URL parameters (?lang=XX&rtl), not in config
 */
const getWidgetConfig = () => {
  return {
    agentId: AGENT_ID,
    apiKey: API_KEY,
  };
};

/**
 * Loads the chat widget with the specified language
 * Uses the VIAWidgetLoader pattern with language support
 */
export const loadChatWidget = (lang: LangE): void => {
  // Remove any existing widget first
  removeChatWidget();

  // Longer delay to ensure cleanup is complete before loading new script
  // This helps prevent conflicts when reloading the widget
  // Increased delay to allow DOM cleanup and script removal to complete
  setTimeout(() => {
    // Create and configure the script element
    const script = document.createElement("script");
    script.setAttribute("data-agent-id", AGENT_ID);
    script.dataset.agentId = AGENT_ID;
    script.dataset.apiKey = API_KEY;
    script.async = true;

    // Build script URL with language parameter according to via.js documentation
    // Documentation: https://via.jumbomail.me/docs - supports ?lang=XX and ?rtl parameters
    const langParam = lang === LangE.HE ? "he" : "en";
    const timestamp = Date.now();
    
    // For Hebrew: use ?lang=he&rtl, for English: use ?lang=en
    if (lang === LangE.HE) {
      script.src = `${WIDGET_BASE_URL}?lang=${langParam}&rtl&t=${timestamp}`;
    } else {
      script.src = `${WIDGET_BASE_URL}?lang=${langParam}&t=${timestamp}`;
    }

    // Initialize widget after script loads
    script.onload = () => {
      setTimeout(() => {
        const WidgetLoader = (window as any).VIAWidgetLoader || (window as any).VIAWidget;
        
        if (WidgetLoader) {
          try {
            const widgetInstance = new WidgetLoader(getWidgetConfig());
            // Store instance for cleanup
            if (widgetInstance && typeof widgetInstance === "object") {
              (window as any).__viaWidgetInstance = widgetInstance;
            }
          } catch (error) {
            console.error("[ChatWidget] Error initializing widget:", error);
          }
        } else {
          console.error("[ChatWidget] WidgetLoader not found!");
        }
      }, 100);
    };

    script.onerror = (error) => {
      console.error("[ChatWidget] Error loading script:", error);
    };

    document.head.appendChild(script);
  }, 200);
};

/**
 * Reloads the chat widget with a new language
 * This is essentially the same as loadChatWidget but semantically clearer
 */
export const reloadChatWidget = (lang: LangE): void => {
  loadChatWidget(lang);
};
